import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface PriceBreak {
  price: number;
  breakValue: number;
  startDate: string | null;
  expireDate: string | null;
  currency?: string;
}

export interface ItemPriceBook {
  itemCode: string;
  /** Display-only currency from the price book API (falls back to basket line currency in the UI). */
  currency?: string;
  priceBreaks: PriceBreak[];
}

/** Parsed `GET /CF/price-info/` payload for a single item + quantity. */
export interface CfPriceInfo {
  bookPriceValue: number;
  priceAfterDiscount: number | null;
  totalDiscount: number | null;
  grossAmount: number | null;
  totalDiscountAmount: number | null;
  total: number | null;
  /** `PriceBook` / `Currency` from the payload; inputs for `POST /CP/cp-pricing-ln/price-book-lines/`. */
  priceBook: string | null;
  currency: string | null;
}

export interface LoadPriceBookLinesOptions {
  /** When true, always POST to LN (Check prices on each open). */
  forceRefresh?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PriceBooksBasketService {
  private apiUrl = environment.apiUrlHost;
  priceCache = new Map<string, ItemPriceBook>();
  private pricesSubject = new BehaviorSubject<Map<string, ItemPriceBook>>(new Map());
  public prices$ = this.pricesSubject.asObservable();

  /** Latest `/CF/price-info/` result per item code (for basket table discount columns). */
  private priceInfoByItem = new Map<string, CfPriceInfo>();
  private priceInfoSubject = new BehaviorSubject<Map<string, CfPriceInfo>>(new Map());
  public priceInfo$ = this.priceInfoSubject.asObservable();
  private priceInfoByKey = new Map<string, CfPriceInfo>();
  private priceInfoInflight = new Map<string, Observable<CfPriceInfo>>();

  /** On-demand `/CP/cp-pricing-ln/price-book-lines/` results, keyed by item + price book + currency. */
  private priceBookLinesByKey = new Map<string, PriceBreak[]>();
  private priceBookLinesInflight = new Map<string, Observable<PriceBreak[]>>();

  constructor(private http: HttpClient) { }

  /** Check if price book exists for an item */
  hasPriceBook(itemCode: string): boolean {
    this.cleanExpired(itemCode); // clean expired first
    return this.priceCache.has(itemCode);
  }

  /** Fetch price book for one or multiple items from backend */
  fetchPriceBook(items: string[]): Observable<any> {
    if (!items || items.length === 0) return of([]);

    const params = new HttpParams().set('item_list', items.join(','));
    return this.http.get<any>(`${this.apiUrl}/CP/basket/prices-by-items/`, { params }).pipe(
      tap((response) => {
        console.log('Price book API response:', response); // Add logging

        const resObj = response as { [itemCode: string]: any[] };

        const itemBooks = Object.entries(resObj).map(([itemCode, breaksRaw]) => {
          const breaks = breaksRaw as any;
          const breakList = Array.isArray(breaks) ? breaks : breaks?.priceBreaks ?? [];
          const currency = (
            breaks?.currency ??
            breakList.find((pb: any) => pb.currency)?.currency ??
            breakList[0]?.currency
          )?.toString?.()?.trim?.();

          return {
            itemCode,
            currency: currency || undefined,
            priceBreaks: breakList.map((pb: any) => ({
              price: pb.price,
              breakValue: pb.break_value,
              startDate: pb.start_date,
              expireDate: pb.end_date,
              currency: pb.currency?.toString?.()?.trim?.() || undefined,
            }))
          };
        });
        console.log('Fetched price book:', itemBooks);
        itemBooks.forEach(itemBook => {
          if (itemBook && itemBook.itemCode && itemBook.priceBreaks) {
            console.log('Adding item to price cache:', itemBook.itemCode);
            this.priceCache.set(itemBook.itemCode, itemBook);
          }
        });

        this.cleanAllExpired();
        this.pricesSubject.next(new Map(this.priceCache));
        console.log('Updated price cache:', Array.from(this.priceCache.keys()));
      }),
      catchError(error => {
        console.error('Failed to fetch price book', error);
        return of([]); // Return empty array instead of throwing error
      })
    );
  }

  /** Get price book for an item from cache (or undefined) */
  getPriceBook(itemCode: string): ItemPriceBook | undefined {
    this.cleanExpired(itemCode);
    return this.priceCache.get(itemCode);
  }

  /** Get price for a quantity for a specific item */
  getPriceForQuantity(itemCode: string, quantity: number, date: Date = new Date()): number | undefined {
    const itemBook = this.getPriceBook(itemCode);
    if (!itemBook) return undefined;

    const validBreaks = itemBook.priceBreaks.filter(pb => {
      const start = pb.startDate ? new Date(pb.startDate) : null;
      const expire = pb.expireDate ? new Date(pb.expireDate) : null;
      if (start && date < start) return false;
      if (expire && date > expire) return false;
      return true;
    });

    if (validBreaks.length === 0) {
      this.removeItem(itemCode); // remove entirely if no valid breaks
      return undefined;
    }

    // Sort by breakValue in ascending order
    validBreaks.sort((a, b) => a.breakValue - b.breakValue);

    // Find the highest break value that the quantity meets or exceeds
    let applicablePrice = undefined;

    for (const pb of validBreaks) {
      if (quantity >= pb.breakValue) {
        applicablePrice = pb.price;
      } else {
        // Stop when we reach a break value higher than our quantity
        break;
      }
    }

    return applicablePrice;
  }

  /**
   * Get accurate price for quantity using the backend API
   * This is the main method that basket service should use for actual pricing
   * `bp` is optional: when omitted the backend resolves the business partner from the session.
   */
  getAccuratePriceForQuantity(itemCode: string, quantity: number, bp?: string | null): Observable<number> {
    return this.getPriceInfo(itemCode, quantity, bp).pipe(
      map((info) => info.bookPriceValue),
    );
  }

  /**
   * Full `CF/price-info` payload for one item (unit price, price after discount, total discount).
   * Dedupes in-flight and completed requests for the same item + quantity + bp.
   */
  getPriceInfo(itemCode: string, quantity: number, bp?: string | null): Observable<CfPriceInfo> {
    const key = this.priceInfoCacheKey(itemCode, quantity, bp);
    const cached = this.priceInfoByKey.get(key);
    if (cached) {
      this.publishPriceInfoForItem(itemCode, cached);
      return of(cached);
    }

    const inflight = this.priceInfoInflight.get(key);
    if (inflight) {
      return inflight;
    }

    let params = new HttpParams()
      .set('itemCode', itemCode)
      .set('quantity', quantity.toString());

    const businessPartner = (bp ?? '').toString().trim();
    if (businessPartner) {
      params = params.set('bp', businessPartner);
    }

    const request = this.http.get<Record<string, unknown>>(`${this.apiUrl}/CF/price-info/`, { params }).pipe(
      map((response) => this.parseCfPriceInfo(response)),
      tap((info) => {
        this.priceInfoByKey.set(key, info);
        this.publishPriceInfoForItem(itemCode, info);
        this.priceInfoInflight.delete(key);
      }),
      catchError((error) => {
        this.priceInfoInflight.delete(key);
        console.error(`Failed to get price info for ${itemCode}:`, error);
        return throwError(() => error);
      }),
      shareReplay(1),
    );

    this.priceInfoInflight.set(key, request);
    return request;
  }

  getCachedPriceInfo(itemCode: string): CfPriceInfo | undefined {
    return this.priceInfoByItem.get(itemCode);
  }

  /**
   * Price breaks for one item, resolved on demand (not preloaded with the basket).
   * Price book and currency come from `/CF/price-info/`, which is skipped when already cached.
   */
  loadPriceBookLinesForItem(
    itemCode: string,
    quantity: number,
    bp?: string | null,
    options?: LoadPriceBookLinesOptions,
  ): Observable<PriceBreak[]> {
    const forceRefresh = options?.forceRefresh === true;
    const cachedInfo = this.getCachedPriceInfo(itemCode);
    if (cachedInfo?.priceBook) {
      return this.fetchPriceBookLines(
        itemCode,
        cachedInfo.priceBook,
        cachedInfo.currency,
        forceRefresh,
      );
    }

    return this.getPriceInfo(itemCode, quantity, bp).pipe(
      switchMap((info) =>
        this.fetchPriceBookLines(itemCode, info.priceBook, info.currency, forceRefresh),
      ),
    );
  }

  /**
   * `POST /CP/cp-pricing-ln/price-book-lines/` — breakpoints for one item.
   * Body: `{ item, price_books: [book], currency }`; only `item` and the book are required.
   */
  fetchPriceBookLines(
    itemCode: string,
    priceBook?: string | null,
    currency?: string | null,
    forceRefresh = false,
  ): Observable<PriceBreak[]> {
    const book = (priceBook ?? '').toString().trim();
    const currencyCode = (currency ?? '').toString().trim();
    const key = `${itemCode}|${book}|${currencyCode}`;

    if (forceRefresh) {
      this.priceBookLinesByKey.delete(key);
      this.priceBookLinesInflight.delete(key);
    } else {
      const cached = this.priceBookLinesByKey.get(key);
      if (cached) {
        return of(cached);
      }

      const inflight = this.priceBookLinesInflight.get(key);
      if (inflight) {
        return inflight;
      }
    }

    const body: Record<string, unknown> = { item: itemCode };
    if (book) {
      body['price_books'] = [book];
    }
    if (currencyCode) {
      body['currency'] = currencyCode;
    }

    const request = this.http
      .post<Record<string, unknown>>(`${this.apiUrl}/CP/cp-pricing-ln/price-book-lines/`, body)
      .pipe(
        map((response) => this.parsePriceBookLines(response)),
        tap((breaks) => {
          this.priceBookLinesByKey.set(key, breaks);
          this.priceBookLinesInflight.delete(key);
        }),
        catchError((error) => {
          this.priceBookLinesInflight.delete(key);
          console.error(`Failed to load price book lines for ${itemCode}:`, error);
          return throwError(() => error);
        }),
        shareReplay(1),
      );

    this.priceBookLinesInflight.set(key, request);
    return request;
  }

  /** Drop cached breakpoints so the next open refetches (e.g. after a quantity change). */
  clearPriceBookLines(itemCode?: string): void {
    if (!itemCode) {
      this.priceBookLinesByKey.clear();
      this.priceBookLinesInflight.clear();
      return;
    }
    for (const key of Array.from(this.priceBookLinesByKey.keys())) {
      if (key.startsWith(`${itemCode}|`)) {
        this.priceBookLinesByKey.delete(key);
        this.priceBookLinesInflight.delete(key);
      }
    }
  }

  private parsePriceBookLines(response: Record<string, unknown> | null | undefined): PriceBreak[] {
    const rows = Array.isArray(response)
      ? response
      : Array.isArray(response?.['breakpoints'])
        ? (response['breakpoints'] as unknown[])
        : [];

    return rows
      .map((row) => {
        const line = (row ?? {}) as Record<string, unknown>;
        return {
          price: this.toFiniteNumber(line['price']) ?? 0,
          breakValue: this.toFiniteNumber(line['break_value']) ?? 0,
          startDate: (line['effective_date'] as string) ?? null,
          expireDate: (line['expiry_date'] as string) ?? null,
          currency: (line['currency'] as string)?.toString?.()?.trim?.() || undefined,
        } as PriceBreak;
      })
      .sort((a, b) => a.breakValue - b.breakValue);
  }

  private priceInfoCacheKey(itemCode: string, quantity: number, bp?: string | null): string {
    return `${itemCode}|${quantity}|${(bp ?? '').toString().trim()}`;
  }

  private publishPriceInfoForItem(itemCode: string, info: CfPriceInfo): void {
    this.priceInfoByItem.set(itemCode, info);
    this.priceInfoSubject.next(new Map(this.priceInfoByItem));
  }

  private parseCfPriceInfo(response: Record<string, unknown> | null | undefined): CfPriceInfo {
    if (!response) {
      throw new Error('Invalid API response');
    }

    const bookPriceValue = this.readNumericField(response, [
      'bookPriceValue',
      'book_price_value',
      'Price',
      'price',
    ]);

    const priceAfterDiscount =
      this.readNumericField(response, [
        'PriceAfterDiscount',
        'priceAfterDiscount',
        'priceAfterDiscountAmount',
        'netPrice',
      ]) ?? this.readNumericFieldContaining(response, 'priceafterdiscount');

    const totalDiscount = this.readNumericField(response, [
      'TotalDiscount',
      'totalDiscount',
    ]);

    const totalDiscountAmount =
      this.readNumericField(response, [
        'totalDiscountAmount',
        'TotalDiscountAmount',
        'total_discount_amount',
      ]);

    const grossAmount =
      this.readNumericField(response, [
        'GrossAmount',
        'grossAmount',
        'gross_amount',
      ]) ?? this.readNumericFieldContaining(response, 'grossamount');

    const total =
      this.readNumericField(response, [
        'total',
        'Total',
        'lineTotal',
        'line_total',
      ]);

    if (
      bookPriceValue === null &&
      priceAfterDiscount === null &&
      totalDiscount === null &&
      grossAmount === null &&
      totalDiscountAmount === null &&
      total === null
    ) {
      throw new Error('Invalid API response');
    }

    return {
      bookPriceValue: bookPriceValue ?? 0,
      priceAfterDiscount,
      totalDiscount,
      grossAmount,
      totalDiscountAmount,
      total,
      priceBook: this.readStringField(response, ['PriceBook']),
      currency: this.readStringField(response, ['Currency']),
    };
  }

  private readStringField(source: Record<string, unknown>, keys: string[]): string | null {
    const normalizedSource = new Map<string, unknown>();
    for (const [key, value] of Object.entries(source)) {
      normalizedSource.set(this.normalizeFieldName(key), value);
    }

    for (const key of keys) {
      const raw = normalizedSource.get(this.normalizeFieldName(key));
      if (typeof raw !== 'string' && typeof raw !== 'number') {
        continue;
      }
      const value = raw.toString().trim();
      if (value) {
        return value;
      }
    }
    return null;
  }

  /**
   * Numeric lookup that ignores casing and separators, so `TotalDiscount`,
   * `totalDiscountAmount`, and `total_discount_amount` all resolve to the same field.
   */
  private readNumericField(source: Record<string, unknown>, keys: string[]): number | null {
    const normalizedSource = new Map<string, unknown>();
    for (const [key, value] of Object.entries(source)) {
      normalizedSource.set(this.normalizeFieldName(key), value);
    }

    for (const key of keys) {
      const numeric = this.toFiniteNumber(normalizedSource.get(this.normalizeFieldName(key)));
      if (numeric !== null) {
        return numeric;
      }
    }
    return null;
  }

  /**
   * Fallback for backend naming we did not anticipate: first numeric field whose
   * normalized name contains the token (e.g. `totalDiscountAmountInOrderCurrency`).
   * Walks nested objects/arrays because LN payloads often wrap the price details.
   */
  private readNumericFieldContaining(
    source: unknown,
    normalizedToken: string,
    depth = 0,
  ): number | null {
    if (depth > 4 || source === null || typeof source !== 'object') {
      return null;
    }

    if (Array.isArray(source)) {
      for (const entry of source) {
        const nested = this.readNumericFieldContaining(entry, normalizedToken, depth + 1);
        if (nested !== null) {
          return nested;
        }
      }
      return null;
    }

    const entries = Object.entries(source as Record<string, unknown>);

    for (const [key, value] of entries) {
      if (!this.normalizeFieldName(key).includes(normalizedToken)) {
        continue;
      }
      const numeric = this.toFiniteNumber(value);
      if (numeric !== null) {
        return numeric;
      }
    }

    for (const [, value] of entries) {
      const nested = this.readNumericFieldContaining(value, normalizedToken, depth + 1);
      if (nested !== null) {
        return nested;
      }
    }

    return null;
  }

  private toFiniteNumber(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const numeric = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private normalizeFieldName(name: string): string {
    return name.replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  /**
   * Get price for quantity with fallback to default price if price book unavailable
   * This method is now primarily for display purposes only
   */
  getPriceForQuantityWithFallback(itemCode: string, quantity: number, defaultPrice: number, date: Date = new Date()): Observable<number> {
    // First, try to get price from local cache
    const localPrice = this.getPriceForQuantity(itemCode, quantity, date);
    if (localPrice !== undefined) {
      return of(localPrice);
    }

    // If not in cache, try to fetch price book first
    return this.fetchPriceBook([itemCode]).pipe(
      switchMap(() => {
        // After fetching, try to get price from price book again
        const priceFromBook = this.getPriceForQuantity(itemCode, quantity, date);

        if (priceFromBook !== undefined) {
          return of(priceFromBook);
        } else {
          // No price book or no valid price found, use the original price from BasketItem
          console.log(`No valid price book found for ${itemCode}, using original price: ${defaultPrice}`);
          return of(defaultPrice);
        }
      }),
      catchError(fetchError => {
        console.warn(`Failed to fetch price book for ${itemCode}, using original price:`, fetchError);
        // Use original price from BasketItem if price book fetch fails
        return of(defaultPrice);
      })
    );
  }

  /** Remove cached price book for an item */
  removeItem(itemCode: string): void {
    if (this.priceCache.has(itemCode)) {
      this.priceCache.delete(itemCode);
      this.pricesSubject.next(new Map(this.priceCache));
    }
  }

  /** Clear all cached prices */
  clear(): void {
    this.priceCache.clear();
    this.pricesSubject.next(new Map());
    this.priceInfoByItem.clear();
    this.priceInfoByKey.clear();
    this.priceInfoInflight.clear();
    this.priceInfoSubject.next(new Map());
    this.clearPriceBookLines();
  }

  /** Remove expired price breaks for a single item */
  private cleanExpired(itemCode: string): void {
    const itemBook = this.priceCache.get(itemCode);
    if (!itemBook) return;

    const now = new Date();
    const validBreaks = itemBook.priceBreaks.filter(pb => {
      const start = pb.startDate ? new Date(pb.startDate) : null;
      const expire = pb.expireDate ? new Date(pb.expireDate) : null;

      // valid only if (no start || already started) && (no expire || not expired)
      return (!start || now >= start) && (!expire || now <= expire);
    });

    if (validBreaks.length === 0) {
      this.removeItem(itemCode);
    } else if (validBreaks.length < itemBook.priceBreaks.length) {
      this.priceCache.set(itemCode, { ...itemBook, priceBreaks: validBreaks });
      this.pricesSubject.next(new Map(this.priceCache));
    }
  }

  /** Remove expired price books for all items */
  private cleanAllExpired(): void {
    Array.from(this.priceCache.keys()).forEach(itemCode => this.cleanExpired(itemCode));
  }
}
